/**
 * Utility functions for generating and handling restaurant slugs
 */

// Cache for slug generation to avoid repeated processing
const slugCache = new Map<string, string>()

/**
 * Convert Vietnamese text to URL-friendly slug (optimized with caching)
 */
export function createSlug(text: string): string {
  // Check cache first
  if (slugCache.has(text)) {
    return slugCache.get(text)!
  }

  const result = text
    .toLowerCase()
    .trim()
    // Single pass Vietnamese character replacement using a more efficient approach
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    // Remove special characters and replace spaces with hyphens
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")

  // Cache the result for future use
  if (slugCache.size > 1000) {
    // Clear cache if it gets too large to prevent memory leaks
    slugCache.clear()
  }
  slugCache.set(text, result)

  return result
}

/**
 * City ID to slug mapping for Vietnamese cities
 */
const CITY_ID_MAPPINGS: Record<number, string> = {
  189: "ho-chi-minh", // Ho Chi Minh City
  190: "ha-noi", // Ha Noi
  191: "da-nang", // Da Nang
  192: "can-tho", // Can Tho
  193: "hai-phong", // Hai Phong
  194: "bien-hoa", // Bien Hoa
  195: "nha-trang", // Nha Trang
  196: "hue", // Hue
  197: "vung-tau", // Vung Tau
  198: "buon-ma-thuot", // Buon Ma Thuot
  199: "quy-nhon", // Quy Nhon
  200: "thai-nguyen", // Thai Nguyen
  201: "thanh-hoa", // Thanh Hoa
  202: "hoa-binh", // Hoa Binh
  203: "vinh", // Vinh
  204: "my-tho", // My Tho
  205: "long-xuyen", // Long Xuyen
  206: "rach-gia", // Rach Gia
  207: "ca-mau", // Ca Mau
  208: "phan-thiet", // Phan Thiet
  209: "dong-hoi", // Dong Hoi
  210: "pleiku", // Pleiku
  211: "kon-tum", // Kon Tum
  212: "yen-bai", // Yen Bai
  213: "lao-cai", // Lao Cai
  214: "dien-bien-phu", // Dien Bien Phu
  215: "son-la", // Son La
  216: "lai-chau", // Lai Chau
  217: "cao-bang", // Cao Bang
  218: "lang-son", // Lang Son
  219: "ha-giang", // Ha Giang
  220: "tuyen-quang", // Tuyen Quang
  221: "bac-kan", // Bac Kan
  222: "thai-binh", // Thai Binh
  223: "nam-dinh", // Nam Dinh
  224: "ninh-binh", // Ninh Binh
  225: "ha-nam", // Ha Nam
  226: "hung-yen", // Hung Yen
  227: "hai-duong", // Hai Duong
  228: "bac-ninh", // Bac Ninh
  229: "bac-giang", // Bac Giang
  230: "quang-ninh", // Quang Ninh
  // Add more cities as needed
}

/**
 * Extract city from address or city_id (preferred)
 */
export function extractCityFromAddress(
  address: string,
  cityId?: number
): string {
  // If city_id is provided, use the mapping (most reliable)
  if (cityId && CITY_ID_MAPPINGS[cityId]) {
    return CITY_ID_MAPPINGS[cityId]
  }

  // Fallback to address parsing for backward compatibility
  const cityPatterns = [
    /(?:tp\.?\s*)?hồ chí minh|ho chi minh|hcm|sài gòn|saigon/i,
    /(?:tp\.?\s*)?hà nội|ha noi|hanoi/i,
    /(?:tp\.?\s*)?đà nẵng|da nang/i,
    /(?:tp\.?\s*)?cần thơ|can tho/i,
    /(?:tp\.?\s*)?hải phòng|hai phong/i,
    /(?:tp\.?\s*)?biên hòa|bien hoa/i,
    /(?:tp\.?\s*)?nha trang/i,
    /(?:tp\.?\s*)?huế|hue/i,
    /(?:tp\.?\s*)?vũng tàu|vung tau/i,
  ]

  const cityMappings: Record<string, string> = {
    "hồ chí minh": "ho-chi-minh",
    "ho chi minh": "ho-chi-minh",
    hcm: "ho-chi-minh",
    "sài gòn": "ho-chi-minh",
    saigon: "ho-chi-minh",
    "hà nội": "ha-noi",
    "ha noi": "ha-noi",
    hanoi: "ha-noi",
    "đà nẵng": "da-nang",
    "da nang": "da-nang",
    "cần thơ": "can-tho",
    "can tho": "can-tho",
    "hải phòng": "hai-phong",
    "hai phong": "hai-phong",
    "biên hòa": "bien-hoa",
    "bien hoa": "bien-hoa",
    "nha trang": "nha-trang",
    huế: "hue",
    hue: "hue",
    "vũng tàu": "vung-tau",
    "vung tau": "vung-tau",
  }

  for (const pattern of cityPatterns) {
    const match = address.match(pattern)
    if (match) {
      const cityName = match[0].toLowerCase().trim()
      return cityMappings[cityName] || createSlug(cityName)
    }
  }

  // Default to ho-chi-minh if no city found
  return "ho-chi-minh"
}

/**
 * Generate restaurant slug from name and optional ID for uniqueness
 */
export function generateRestaurantSlug(name: string, id?: number): string {
  const baseSlug = createSlug(name)
  return id ? `${baseSlug}-${id}` : baseSlug
}

/**
 * Extract restaurant ID from slug (assumes format: name-id)
 */
export function extractIdFromSlug(slug: string): number | null {
  const parts = slug.split("-")
  const lastPart = parts[parts.length - 1]
  const id = parseInt(lastPart, 10)
  return isNaN(id) ? null : id
}

/**
 * Generate full restaurant URL path
 */
export function generateRestaurantUrl(
  restaurantName: string,
  restaurantAddress: string,
  restaurantId: number,
  cityId?: number
): string {
  const city = extractCityFromAddress(restaurantAddress, cityId)
  const restaurantSlug = generateRestaurantSlug(restaurantName, restaurantId)
  return `/${city}/${restaurantSlug}`
}

/**
 * Parse restaurant URL to extract city and restaurant slug
 */
export function parseRestaurantUrl(url: string): {
  city: string
  restaurantSlug: string
  restaurantId: number | null
} | null {
  const parts = url.split("/").filter(Boolean)
  if (parts.length !== 2) return null

  const [city, restaurantSlug] = parts
  const restaurantId = extractIdFromSlug(restaurantSlug)

  return {
    city,
    restaurantSlug,
    restaurantId,
  }
}

/**
 * Get city name from city ID for display purposes
 */
export function getCityNameFromId(cityId: number): string {
  const citySlugMappings: Record<number, string> = {
    189: "Hồ Chí Minh",
    190: "Hà Nội",
    191: "Đà Nẵng",
    192: "Cần Thơ",
    193: "Hải Phòng",
    194: "Biên Hòa",
    195: "Nha Trang",
    196: "Huế",
    197: "Vũng Tàu",
    198: "Buôn Ma Thuột",
    199: "Quy Nhon",
    200: "Thái Nguyên",
    201: "Thanh Hóa",
    202: "Hòa Bình",
    203: "Vinh",
    204: "Mỹ Tho",
    205: "Long Xuyên",
    206: "Rạch Giá",
    207: "Cà Mau",
    208: "Phan Thiết",
    209: "Đồng Hới",
    210: "Pleiku",
    211: "Kon Tum",
    212: "Yên Bái",
    213: "Lào Cai",
    214: "Điện Biên Phủ",
    215: "Sơn La",
    216: "Lai Châu",
    217: "Cao Bằng",
    218: "Lạng Sơn",
    219: "Hà Giang",
    220: "Tuyên Quang",
    221: "Bắc Kạn",
    222: "Thái Bình",
    223: "Nam Định",
    224: "Ninh Bình",
    225: "Hà Nam",
    226: "Hưng Yên",
    227: "Hải Dương",
    228: "Bắc Ninh",
    229: "Bắc Giang",
    230: "Quảng Ninh",
  }

  return citySlugMappings[cityId] || "Hồ Chí Minh"
}
